import { useEffect, useMemo, useState } from 'react';
import type { Correction, FailureCategory } from '@sr-test-wizard/shared';
import { VERDICT_LABEL } from '@sr-test-wizard/shared';
import { evalApi } from '../../lib/evalApi';

const CATEGORY_LABEL: Record<string, string> = {
  recognition: 'Recognition (ontology miss → add alias/branding)',
  observation: 'Observation (vision/video gap → fix capture)',
  judgment: 'Judgment (reasoned wrong → fix invariant/disambiguation)',
  data: 'Data (bad REQ/test → fix guidance/Jira)',
  uncategorised: 'Uncategorised',
};

const CATEGORIES: FailureCategory[] = ['recognition', 'observation', 'judgment', 'data'];

export function CorrectionsPage() {
  const [rows, setRows] = useState<Correction[]>([]);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<FailureCategory | 'all'>('all');

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, sum] = await Promise.all([
        evalApi.listCorrections(),
        evalApi.correctionsSummary(),
      ]);
      setRows(list);
      setSummary(sum);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const filtered = useMemo(
    () => (active === 'all' ? rows : rows.filter((r) => (r.failureCategory ?? 'uncategorised') === active)),
    [rows, active],
  );

  return (
    <div className="flex-col" style={{ gap: 14 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>Corrections</h1>
        <span className="spacer" />
        <button type="button" className="btn small" onClick={() => void refresh()}>Refresh</button>
      </div>

      <p className="muted small" style={{ marginTop: 0 }}>
        Every time a tester overrides the AI verdict and classifies why, a labelled correction is captured here.
        Grouping by failure category tells you which LAYER to invest in next. Only <strong>recognition</strong>
        misses loop back to the ontology; the others point at vision capture, the invariant/disambiguation, or the
        underlying REQ/test. No model training is involved — this is measurement + diagnosis.
      </p>

      {error && <div className="card" style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }}>{error}</div>}

      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className={`btn small ${active === 'all' ? 'btn-primary' : ''}`}
          onClick={() => setActive('all')}
        >
          All ({rows.length})
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            className={`btn small ${active === c ? 'btn-primary' : ''}`}
            onClick={() => setActive(c)}
            title={CATEGORY_LABEL[c]}
          >
            {c} ({summary[c] ?? 0})
          </button>
        ))}
        {(summary.uncategorised ?? 0) > 0 && (
          <span className="muted small">+ {summary.uncategorised} uncategorised</span>
        )}
      </div>

      {loading ? (
        <div className="muted">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card"><p className="muted" style={{ margin: 0 }}>No corrections captured yet.</p></div>
      ) : (
        <div className="flex-col" style={{ gap: 8 }}>
          {filtered.map((r) => (
            <div className="card" key={r.id} style={{ padding: 12 }}>
              <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span className="badge small mono">{r.testKey}</span>
                {r.game && <span className="muted small">{r.game}</span>}
                <span className="spacer" />
                <span className="badge small">{r.failureCategory ?? 'uncategorised'}</span>
                <span className="muted small">{new Date(r.createdAt).toLocaleString()}</span>
              </div>
              <div className="small" style={{ marginTop: 6 }}>
                AI said <strong>{r.aiVerdict ? VERDICT_LABEL[r.aiVerdict] : '—'}</strong>; tester set{' '}
                <strong>{r.humanVerdict ? VERDICT_LABEL[r.humanVerdict] : '—'}</strong>.
              </div>
              {r.explanation && (
                <div className="small" style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{r.explanation}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
